/** @file reparentNode: drops a node under a new parent, refusing the root and cycles with a message. */
import type { EditorStore } from '../../state/editorTypes';

/** True when `ancestor` is `name` itself or one of its ancestors in `nodes`. */
function isOnPathUp(nodes: Record<string, { parent?: string }>, name: string, ancestor: string): boolean {
  const limit = Object.keys(nodes).length;
  for (let at: string | undefined = name, steps = 0; at !== undefined && steps <= limit; at = nodes[at]?.parent, steps++) {
    if (at === ancestor) return true;
  }
  return false;
}

/**
 * Dispatches `node.reparent { name, parent, keepWorld: true }` (the drag keeps its behaviour, design 19.8 D3). Refuses,
 * with a warning toast and no command, a drop on the node itself or inside its own subtree (a cycle) and a drag of the
 * root. A drop onto a mirrored twin's subtree is an ordinary drop. Returns whether it dispatched.
 */
export function reparentNode(store: EditorStore, name: string, parent: string): boolean {
  const s = store.getState();
  const nodes = s.puppet.doc?.puppet.nodes ?? {};
  const current = nodes[name]?.parent;
  if (current === undefined) {
    s.notify('warning', `"${name}" is the root; it cannot move`);
    return false;
  }
  if (isOnPathUp(nodes, parent, name)) {
    s.notify('warning', `Cannot move "${name}" under "${parent}": that would make a loop (${parent} hangs below ${name})`);
    return false;
  }
  if (current === parent) return false;
  void s.dispatch([{ name: 'node.reparent', input: { name, parent, keepWorld: true } }], `reparent ${name}`);
  return true;
}
